<template>
  <app-modal :modal-id="modalId" :title="modalTitle" :preloader="preloader"
                @submit="submit" @close-modal="closeModal" modal-size="large">
        <template slot="header">
            <h5 class="modal-title" id="ModalLabel">{{ modalTitle }}</h5>
            <button type="button" class="close outline-none" @click.prevent="closeModal">
                <span>
                    <app-icon :name="'x'"></app-icon>
                </span>
            </button>
        </template>
        <template slot="body">
            <app-overlay-loader v-if="preloader" />
            <form class="mb-0" :class="{ 'loading-opacity': preloader }" ref="form"
                :data-url='selectedUrl ? `working-shifts/${formData.id}` : `working-shifts`'>
                <app-form-group
                    :label="$t('name')"
                    type="text"
                    v-model="formData.name"
                    :placeholder="$t('name')"
                    :required="true"
                    :error-message="$errorMessage(errors, 'name')"
                />
                <div class="row">
                    <div class="col-md-6">
                        <app-form-group
                            :disabled="!!formData.is_default"
                            :label="$t('start_date')"
                            type="date"
                            v-model="formData.start_date"
                            :placeholder="$t('start_date')"
                            :required="true"
                            :error-message="$errorMessage(errors, 'start_date')"
                        />
                    </div>
                    <div class="col-md-6">
                        <app-form-group
                            :disabled="!!formData.is_default"
                            :label="$t('end_date')"
                            type="date"
                            v-model="formData.end_date"
                            :placeholder="$t('end_date')"
                            :error-message="$errorMessage(errors, 'end_date')"
                        />
                    </div>
                </div>

                <div class="form-group d-flex flex-column flex-md-row align-items-md-center my-primary">
                    <label class="mr-md-3 mb-md-0" v-if="checkDisable">{{ $fieldTitle('working_shift_type', '') }}</label>
                    <label class="mr-md-3 mb-md-0" v-else>{{ $fieldTitle('choose_a_working_shift_type', '') }}</label>
                    <div>
                        <app-input
                            type="radio"
                            :list="[
                                {id: 'regular', value: $t('regular')},
                                {id: 'scheduled', value:  $t('scheduled')}
                            ]"
                            v-model="formData.type"
                            :error-message="$errorMessage(errors, 'type')"
                        />
                    </div>
                </div>

                <!--work shift type == regular -->
                <div v-if="formData.type === 'regular'">
                    <div class="mt-4">
                        <label v-if="checkDisable">{{ $fieldTitle('regular_week', '') }}</label>
                        <label v-else>{{ $fieldTitle('set_regular_week', '') }}</label>
                        <small class="text-muted">
                            ({{ $fieldTitle('set_week_with_fixed_time', '') }})
                        </small>
                    </div>
                    <div class="row">
                        <div class="col-md-6">
                            <div class="form-group">
                                <label>{{ $fieldTitle('start_time', '') }}</label>
                                <app-input
                                    :id="'start_at'"
                                    type="time"
                                    v-model="formData.start_at"
                                    :placeholder="$t('start_time')"
                                    name="start_time"
                                    :error-message="$errorMessage(errors, 'start_at')"
                                />
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="form-group">
                                <label>{{ $fieldTitle('end_time', '') }}</label>
                                <app-input
                                    :id="'end-at'"
                                    type="time"
                                    v-model="formData.end_at"
                                    :placeholder="$t('end_time')"
                                    :error-message="$errorMessage(errors, 'end_at')"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Champs supplémentaires pour les pauses et temps de grâce -->
                    <div v-if="formData.start_at && formData.end_at" class="row mb-3">
                        <div class="col-md-4">
                            <div class="form-group">
                                <label>{{ $t('break_minutes') }}</label>
                                <app-input
                                    type="number"
                                    min="0"
                                    max="480"
                                    v-model="formData.break_minutes"
                                    :placeholder="$t('break_minutes')"
                                    :error-message="$errorMessage(errors, 'break_minutes')"
                                />
                            </div>
                        </div>
                        <div class="col-md-4">
                            <div class="form-group">
                                <label>{{ $t('grace_time_before') }}</label>
                                <app-input
                                    type="number"
                                    min="0"
                                    max="60"
                                    v-model="formData.grace_time_before"
                                    :placeholder="$t('grace_time_before')"
                                    :error-message="$errorMessage(errors, 'grace_time_before')"
                                />
                            </div>
                        </div>
                        <div class="col-md-4">
                            <div class="form-group">
                                <label>{{ $t('grace_time_after') }}</label>
                                <app-input
                                    type="number"
                                    min="0"
                                    max="60"
                                    v-model="formData.grace_time_after"
                                    :placeholder="$t('grace_time_after')"
                                    :error-message="$errorMessage(errors, 'grace_time_after')"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="my-3">
                        <h6 v-if="checkDisable" class="text-danger">{{ $fieldTitle('weekend_days', '') }}</h6>
                        <h6 v-else class="text-danger">{{ $fieldTitle('select_your_weekend_days', '') }}</h6>
                    </div>
                    <div class="mb-primary">
                        <app-input
                            type="checkbox"
                            :list="weekdays"
                            v-model="formData.weekdays"
                            radio-checkbox-wrapper="row"
                            custom-checkbox-type="checkbox-default col-md-3"
                            :error-message="$errorMessage(errors, 'weekdays')"
                        />
                    </div>
                </div>

                <div v-else>
                    <div class="mt-4">
                        <label v-if="checkDisable">{{ $fieldTitle('scheduled_week', '') }}</label>
                        <label v-else>{{ $fieldTitle('set_scheduled_week', '') }}</label>
                        <small class="text-muted">
                            ({{
                                $fieldTitle('set_week_with_customized_time_and_without_time_and_without_time_it_will_be_weekend', '')
                            }})
                        </small>
                    </div>
                    <div class="row mt-4">
                        <div v-for="(item, index) in formData.details" class="col-md-6">
                            <label>
                                {{ $t(item.weekday) }}
                                <span class="text-muted">
                                    {{ !(item.start_at && item.end_at) ? `(${$t('weekend')})` : '' }}
                                </span>
                            </label>
                            <div class="form-row">
                                <div class="col-md-6">
                                    <div class="form-group">
                                        <app-input
                                            :id="'start-at-'+index"
                                            type="time"
                                            v-model="item.start_at"
                                            :placeholder="$t('start_time')"
                                            :error-message="errorMessageForArray(
                                                errors,
                                            `details.${index}.start_at`,
                                            `details.${index}.end at`
                                            )"
                                        />
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="from-group">
                                        <app-input
                                            :id="'end-at-'+index"
                                            type="time"
                                            v-model="item.end_at"
                                            :placeholder="$t('end_time')"
                                            :error-message="errorMessageForArray(
                                                errors,
                                                `details.${index}.end_at`,
                                                `details.${index}.start at`
                                            )"
                                        />
                                    </div>
                                </div>
                            </div>
                            
                            <!-- Champs supplémentaires pour les pauses et temps de grâce -->
                            <div v-if="item.start_at && item.end_at" class="form-row mt-2">
                                <div class="col-md-4">
                                    <div class="form-group">
                                        <label class="small text-muted">{{ $t('break_minutes') }}</label>
                                        <app-input
                                            :id="'break-minutes-'+index"
                                            type="number"
                                            min="0"
                                            max="480"
                                            v-model="item.break_minutes"
                                            :placeholder="$t('break_minutes')"
                                        />
                                    </div>
                                </div>
                                <div class="col-md-4">
                                    <div class="form-group">
                                        <label class="small text-muted">{{ $t('grace_time_before') }}</label>
                                        <app-input
                                            :id="'grace-before-'+index"
                                            type="number"
                                            min="0"
                                            max="60"
                                            v-model="item.grace_time_before"
                                            :placeholder="$t('grace_time_before')"
                                        />
                                    </div>
                                </div>
                                <div class="col-md-4">
                                    <div class="form-group">
                                        <label class="small text-muted">{{ $t('grace_time_after') }}</label>
                                        <app-input
                                            :id="'grace-after-'+index"
                                            type="number"
                                            min="0"
                                            max="60"
                                            v-model="item.grace_time_after"
                                            :placeholder="$t('grace_time_after')"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Location Select Input -->
                <div class="form-group">
                    <label for="location">{{ $t('location') }}</label>
                    <div class="d-flex align-items-center w-100">
                        <app-input class="w-100" id="rental" v-model="formData.location_id" 
                                :list="locationList" list-value-field="name" :placeholder="$t('choose_one')" 
                                type="search-select" :required="true" />
                    </div>
                </div>

                <!-- <app-form-group
                    type="textarea"
                    :label="$fieldLabel('description', '')"
                    :placeholder="`${checkDisable ? '' : $textAreaPlaceHolder('description')}`"
                    v-model="formData.description"
                    :required="true"
                    :error-message="$errorMessage(errors, 'description')"
                /> -->

            </form>
        </template>
        <template slot="footer">
            <button type="button" class="btn btn-secondary mr-2" data-dismiss="modal" @click="closeModal">
                {{ $t('cancel') }}
            </button>
            <button type="button" class="btn btn-primary" @click="submit">{{ $t('save') }}</button>
        </template>
    </app-modal>
</template>

<script>
import { ModalMixin } from '../../../Mixins/ModalMixin';
import {errorMessageForArray} from '../../../../common/Helper/Support/FormHelper';
import FormHelperMixins from "../../../../common/Mixin/Global/FormHelperMixins";
import {formatDateForServer, formatUtcToLocal} from "../../../../common/Helper/Support/DateTimeHelper";
import {cloneDeep} from 'lodash';
import {mapState} from 'vuex';
import moment from 'moment';

export default {
    name: 'WorkingShiftAddEditModal',
    mixins: [FormHelperMixins, ModalMixin],
    props: {
        tableId: {
            type: String,
        },
    },
    data() {
        return {
            preloader: false,
            errorMessageForArray,
            modalId: 'working-shift-add-edit-modal',
            weekdays: [
                {id: "mon", value: this.$t("monday")},
                {id: "tue", value: this.$t("tuesday")},
                {id: "wed", value: this.$t("wednesday")},
                {id: "thu", value: this.$t("thursday")},
                {id: "fri", value: this.$t("friday")},
                {id: "sat", value: this.$t("saturday")},
                {id: "sun", value: this.$t("sunday")},
            ],
            formData: {
                name: '',
                type: 'regular',
                location_id: null,
                weekdays: [],
                attendances_count: 0,
                break_minutes: 0,
                grace_time_before: 0,
                grace_time_after: 0,
                details: [
                    {
                        weekday: 'mon',
                        start_at: '',
                        end_at: '',
                        break_minutes: 0,
                        grace_time_before: 0,
                        grace_time_after: 0,
                        is_weekend: 0
                    },
                    {
                        weekday: 'tue',
                        start_at: '',
                        end_at: '',
                        break_minutes: 0,
                        grace_time_before: 0,
                        grace_time_after: 0,
                        is_weekend: 0
                    },
                    {
                        weekday: 'wed',
                        start_at: '',
                        end_at: '',
                        break_minutes: 0,
                        grace_time_before: 0,
                        grace_time_after: 0,
                        is_weekend: 0
                    },
                    {
                        weekday: 'thu',
                        start_at: '',
                        end_at: '',
                        break_minutes: 0,
                        grace_time_before: 0,
                        grace_time_after: 0,
                        is_weekend: 0
                    },
                    {
                        weekday: 'fri',
                        start_at: '',
                        end_at: '',
                        break_minutes: 0,
                        grace_time_before: 0,
                        grace_time_after: 0,
                        is_weekend: 0
                    },
                    {
                        weekday: 'sat',
                        start_at: '',
                        end_at: '',
                        break_minutes: 0,
                        grace_time_before: 0,
                        grace_time_after: 0,
                        is_weekend: 0
                    },
                    {
                        weekday: 'sun',
                        start_at: '',
                        end_at: '',
                        break_minutes: 0,
                        grace_time_before: 0,
                        grace_time_after: 0,
                        is_weekend: 0
                    },
                ],
            },
            isModalActive: false,
            modalTitle: `${this.$t('new')} ${this.$t('working-shift')}`,
        }
    },
    computed:{
        checkDisable () {
            return this.selectedUrl ? (this.formData.attendances_count > 0 || !this.$can('update_working_shifts') || this.viewOnly ):
              !this.$can('create_working_shifts')
        },
        ...mapState({
            locationList: state => state.selects.locationList,
        })
    },
    methods: {
        submit() {
            this.loading = true;
            this.message = '';
            this.errors = {};

            let formData = {};
            if (this.formData.type === 'regular') {
                formData = this.prepareRegularType(this.formData);
            } else {
                formData = this.prepareSchedulerType(this.formData);
            }

            formData.start_date = formatDateForServer(formData.start_date);
            formData.end_date = formatDateForServer(formData.end_date);

            this.save(formData);
        },
        afterSuccess({data}) {
            this.formData = {};
            $('#working-shift-modal').modal('hide')
            this.toastAndReload(data.message, 'working-shifts-table');
        },
        afterSuccessFromGetEditData({data}) {
            this.formData = data;
            this.formData.start_date = data.start_date ? new Date(data.start_date) : '';
            this.formData.end_date = data.end_date ? new Date(data.end_date) : '';
            this.formData.start_at = data.start_at ? moment(data.start_at, 'HH:mm:ss').format('HH:mm') : '';
            this.formData.end_at = data.end_at ? moment(data.end_at, 'HH:mm:ss').format('HH:mm') : '';
            
            // Charger les nouveaux champs
            this.formData.break_minutes = data.break_minutes || 0;
            this.formData.grace_time_before = data.grace_time_before || 0;
            this.formData.grace_time_after = data.grace_time_after || 0;
            
            this.formData.weekdays = data.details.filter(week => {
                return parseInt(week.is_weekend);
            }).map(week => {
                return week.weekday;
            });

            this.formData.details = data.details.map(details => {
                return {
                    ...details,
                    start_at: details.start_at ? moment(details.start_at, 'HH:mm:ss').format('HH:mm') : '',
                    end_at: details.end_at ? moment(details.end_at, 'HH:mm:ss').format('HH:mm') : '',
                    break_minutes: details.break_minutes || 0,
                    grace_time_before: details.grace_time_before || 0,
                    grace_time_after: details.grace_time_after || 0,
                }
            });

            this.preloader = false;
        },
        prepareRegularType(formData) {
            let data = cloneDeep(formData);

            if (!(data.start_at && data.end_at)) {
                return data;
            }

            data.details.map(day => {
                if (data.weekdays.includes(day.weekday)) {
                    day.is_weekend = 1;
                    return day;
                }

                day.end_at = data.end_at ? moment(data.end_at, 'HH:mm').format('HH:mm:ss') : '';
                day.start_at = data.start_at ? moment(data.start_at, 'HH:mm').format('HH:mm:ss') : '';
                day.break_minutes = data.break_minutes || 0;
                day.grace_time_before = data.grace_time_before || 0;
                day.grace_time_after = data.grace_time_after || 0;
                day.is_weekend = data.weekdays.includes(day.weekday) ? 1 : 0
                return day;
            });

            return data;
        },
        prepareSchedulerType(formData) {
            let data = cloneDeep(formData);
            data.details.map(day => {
                day.start_at = day.start_at ? moment(day.start_at, 'HH:mm').format('HH:mm:ss') : '';
                day.end_at = day.end_at ? moment(day.end_at, 'HH:mm').format('HH:mm:ss') : '';
                day.break_minutes = day.break_minutes || 0;
                day.grace_time_before = day.grace_time_before || 0;
                day.grace_time_after = day.grace_time_after || 0;
                day.is_weekend = !!(day.start_at && day.end_at) ? 0 : 1
                return day;
            })
            return data;
        }
    },
    mounted() {
        this.$store.dispatch('getLocations');
    },
}
</script>

<style>

</style>